import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { User, Calendar, MapPin, Tag, QrCode, Shield, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BadgePage() {
  const { qrToken } = useParams();
  const [badge, setBadge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBadge = async () => {
      try {
        const res = await axios.get(`${API}/api/public/badge/${qrToken}`);
        setBadge(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Badge introuvable");
      } finally {
        setLoading(false);
      }
    };
    if (qrToken) fetchBadge();
  }, [qrToken]);

  if (loading) return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
      <Loader2 className="h-8 w-8 text-[#0099CC] animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center p-6">
      <div className="text-center">
        <Shield className="h-16 w-16 text-red-500/50 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-white mb-2">Badge invalide</h1>
        <p className="text-gray-500">{error}</p>
      </div>
    </div>
  );

  const b = badge;
  const isVIP = b.category === "VIP" || b.is_vip;
  const isCheckedIn = b.status === "checked_in";
  const isCancelled = b.status === "cancelled";

  const accentColor = isVIP ? "#F59E0B" : "#0099CC";
  const statusText = isCheckedIn ? "Present" : isCancelled ? "Annule" : "Inscrit";
  const statusColor = isCheckedIn ? "#10B981" : isCancelled ? "#EF4444" : "#0099CC";

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center p-4" data-testid="badge-page">
      <div className="w-full max-w-sm">
        {/* Badge Card */}
        <div
          className="rounded-3xl overflow-hidden shadow-2xl"
          style={{ background: `linear-gradient(180deg, ${accentColor}15 0%, #131B2E 30%)`, border: `1px solid ${accentColor}30` }}
          data-testid="badge-card"
        >
          {/* Top accent bar */}
          <div className="h-2" style={{ background: accentColor }} />

          {/* Event info */}
          <div className="px-6 pt-5 pb-3 text-center border-b border-[#2A3441]/50">
            <p className="text-[10px] uppercase tracking-[3px] font-medium" style={{ color: accentColor }}>
              {isVIP ? "VIP ACCESS" : "PARTICIPANT"}
            </p>
            <h2 className="text-white font-bold text-lg mt-2 leading-tight" data-testid="badge-event-title">{b.event_title}</h2>
            <div className="flex items-center justify-center gap-3 mt-2 text-xs text-gray-400">
              {b.event_date && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {new Date(b.event_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
                </span>
              )}
              {b.event_location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {b.event_location}
                </span>
              )}
            </div>
          </div>

          {/* Participant */}
          <div className="px-6 py-5 text-center">
            <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center text-2xl font-bold" style={{ background: `${accentColor}20`, color: accentColor }}>
              {(b.first_name?.[0] || "").toUpperCase()}{(b.last_name?.[0] || "").toUpperCase()}
            </div>
            <h1 className="text-white text-xl font-bold mt-3" data-testid="badge-name">
              {b.first_name} {b.last_name}
            </h1>
            {b.organization && <p className="text-gray-400 text-sm mt-1">{b.organization}</p>}

            {/* Category badge */}
            <div className="flex justify-center gap-2 mt-3">
              <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: `${accentColor}20`, color: accentColor }}>
                <Tag className="h-3 w-3 inline mr-1" />{b.category || "Standard"}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: `${statusColor}20`, color: statusColor }}>
                {statusText}
              </span>
            </div>
          </div>

          {/* QR Code */}
          <div className="px-6 pb-6">
            <div className="bg-white rounded-2xl p-4 text-center" data-testid="badge-qr">
              {b.qr_base64 ? (
                <img src={`data:image/png;base64,${b.qr_base64}`} alt="QR Code" className="w-48 h-48 mx-auto" />
              ) : (
                <div className="w-48 h-48 mx-auto flex items-center justify-center bg-gray-100 rounded-xl">
                  <QrCode className="h-16 w-16 text-gray-300" />
                </div>
              )}
              <p className="text-gray-600 text-[10px] mt-2 font-mono tracking-wider">{b.reference}</p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 pb-4 text-center">
            <p className="text-[9px] text-gray-600 uppercase tracking-wider">Presentez ce badge a l'entree</p>
            <p className="text-[8px] text-gray-700 mt-1">GLOTECH Event Center</p>
          </div>
        </div>
      </div>
    </div>
  );
}
